import { useState, useRef, useEffect } from 'react';
import { fontStack } from '../styles/shared';

/**
 * Custom themed dropdown that replaces native <select>.
 * Props:
 *   value       - current selected value
 *   onChange     - (value) => void
 *   options      - [{ value, label }]
 *   placeholder  - placeholder text when no value
 *   showAllOption - if true, shows "All" reset option (for filters)
 *   allLabel     - label for the All option (default "All")
 *   searchable   - if true, shows a text input to filter options
 *   style        - additional style overrides on the trigger
 */
export default function CustomSelect({ value, onChange, options, placeholder, showAllOption, allLabel, searchable, style }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);
    const searchRef = useRef(null);
    const selected = options.find(o => o.value === value);

    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    useEffect(() => {
        if (open && searchable && searchRef.current) {
            searchRef.current.focus();
        }
        if (!open) setSearch('');
    }, [open, searchable]);

    const filtered = searchable && search
        ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
        : options;

    return (
        <div ref={ref} style={{ position: 'relative', ...style }}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '11px 14px',
                    border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: '8px', fontSize: '14px',
                    background: 'var(--input-bg)',
                    color: selected ? 'var(--text)' : 'var(--text-muted)',
                    cursor: 'pointer', fontFamily: fontStack,
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.15s',
                    textAlign: 'left',
                }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selected ? selected.label : (placeholder || 'Select...')}
                </span>
                <svg width="12" height="7" viewBox="0 0 12 7" fill="none" style={{ flexShrink: 0, transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none', marginLeft: '8px' }}>
                    <path d="M1 1L6 6L11 1" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 100,
                    maxHeight: '260px', overflowY: 'auto',
                    background: 'var(--card-bg)', border: '1px solid var(--border)',
                    borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    padding: '4px',
                }}>
                    {searchable && (
                        <div style={{ padding: '4px 4px 6px', position: 'sticky', top: 0, background: 'var(--card-bg)', zIndex: 1 }}>
                            <input
                                ref={searchRef}
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Type to search..."
                                style={{
                                    width: '100%', padding: '8px 10px', fontSize: '13px',
                                    border: '1px solid var(--border)', borderRadius: '6px',
                                    background: 'var(--input-bg)', color: 'var(--text)',
                                    outline: 'none', fontFamily: fontStack, boxSizing: 'border-box',
                                }}
                                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                                onBlur={e => e.target.style.borderColor = 'var(--border)'}
                            />
                        </div>
                    )}
                    {showAllOption && (
                        <div
                            onClick={() => { onChange(''); setOpen(false); }}
                            style={{
                                padding: '9px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: !value ? 600 : 400, cursor: 'pointer',
                                color: !value ? 'var(--accent)' : 'var(--text-muted)',
                                background: !value ? 'rgba(0,174,239,0.06)' : 'transparent',
                                transition: 'background 0.1s',
                            }}
                            onMouseEnter={e => { if (value) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                            onMouseLeave={e => { if (value) e.currentTarget.style.background = 'transparent'; }}
                        >
                            {allLabel || 'All'}
                        </div>
                    )}
                    {filtered.map(opt => (
                        <div
                            key={opt.value}
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                            style={{
                                padding: '9px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: value === opt.value ? 600 : 400, cursor: 'pointer',
                                color: value === opt.value ? 'var(--accent)' : 'var(--text)',
                                background: value === opt.value ? 'rgba(0,174,239,0.06)' : 'transparent',
                                transition: 'background 0.1s',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}
                            onMouseEnter={e => { if (value !== opt.value) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                            onMouseLeave={e => { if (value !== opt.value) e.currentTarget.style.background = 'transparent'; }}
                        >
                            {opt.label}
                            {value === opt.value && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            )}
                        </div>
                    ))}
                    {searchable && filtered.length === 0 && (
                        <div style={{ padding: '12px', fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center' }}>
                            No results
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
